// src/components/InfiniteSelect.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { ChevronDown, Search, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

export interface SelectOption {
  id: string
  label: string
  value: string
  avatar?: string
  subtitle?: string
  [key: string]: unknown
}

export interface ApiResponse<T> {
  data: {
    result: T[]
    page: number
    totalPages: number
    hasNextPage: boolean
    totalCount: number
  }
}

export interface ApiParams {
  page: number
  limit: number
  search?: string
  [key: string]: unknown
}

interface InfiniteSelectProps<T = unknown> {
  queryKey: readonly string[] | string[]
  queryFn: (params: ApiParams) => Promise<ApiResponse<T>>
  transformData: (item: T) => SelectOption
  value?: SelectOption | SelectOption[] | null
  onValueChange: (value: SelectOption | SelectOption[] | null) => void
  placeholder?: string
  label?: string
  required?: boolean
  className?: string
  error?: string
  disabled?: boolean
  searchPlaceholder?: string
  emptyMessage?: string
  noResultsMessage?: string
  pageSize?: number
  additionalParams?: Record<string, unknown>
  renderOption?: (option: SelectOption) => React.ReactNode
  renderValue?: (option: SelectOption | SelectOption[]) => React.ReactNode
  enableSearch?: boolean
  showAvatar?: boolean
  preloadData?: boolean
  multiple?: boolean
  maxSelections?: number
  disabledOptions?: string[]
  showTooltip?: boolean
  filterValues?: (value: T) => boolean
}

export function InfiniteSelect<T = unknown>({
  queryKey,
  queryFn,
  transformData,
  value,
  onValueChange,
  placeholder = 'Select option...',
  label,
  required = false,
  className = '',
  error,
  disabled = false,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options available',
  noResultsMessage = 'No results found',
  pageSize = 20,
  additionalParams = {},
  renderOption,
  renderValue,
  enableSearch = true,
  showAvatar = false,
  preloadData = true,
  multiple = false,
  maxSelections,
  disabledOptions = [],
  showTooltip = false,
  filterValues,
}: Readonly<InfiniteSelectProps<T>>) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const observerRef = useRef<HTMLDivElement>(null)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  let selectedOptions: SelectOption[] = []
  if (multiple) {
    selectedOptions = Array.isArray(value) ? value : []
  } else if (value && !Array.isArray(value)) {
    selectedOptions = [value]
  }

  const isSelected = useCallback(
    (option: SelectOption) => {
      return selectedOptions.some(selected => selected.id === option.id)
    },
    [selectedOptions]
  )

  const canSelectMore = useCallback(() => {
    if (!multiple || !maxSelections) return true
    return selectedOptions.length < maxSelections
  }, [multiple, maxSelections, selectedOptions.length])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error: queryError,
    refetch,
    isFetched,
  } = useInfiniteQuery<ApiResponse<T>, Error, ApiResponse<T>, string[], number>(
    {
      queryKey: [
        ...queryKey,
        debouncedSearchTerm,
        JSON.stringify(additionalParams),
      ],
      queryFn: ({ pageParam = 1 }) =>
        queryFn({
          page: Number(pageParam),
          limit: pageSize,
          search: debouncedSearchTerm || undefined,
          ...additionalParams,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage: ApiResponse<T>) => {
        if (lastPage?.data.hasNextPage) {
          return lastPage.data.page + 1
        }
        return undefined
      },
      enabled: preloadData || open,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (isFetched && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [isFetched, hasInitiallyLoaded])

  const options: SelectOption[] = React.useMemo(() => {
    if (!data) return []

    if ('pages' in data && Array.isArray(data.pages)) {
      return data.pages.flatMap((page: ApiResponse<T>) => {
        const results = filterValues
          ? page?.data?.result?.filter(filterValues)
          : page?.data?.result

        return results.map(transformData)
      })
    }
    return []
  }, [data, transformData])

  useEffect(() => {
    const currentObserver = observerRef.current
    if (!currentObserver || !hasNextPage || isFetchingNextPage || !open) return

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        root: multiple ? currentObserver.closest('.overflow-y-auto') : null,
        rootMargin: '50px',
        threshold: 0.1,
      }
    )

    observer.observe(currentObserver)

    return () => {
      observer.disconnect()
    }
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    open,
    multiple,
    options.length,
  ])

  const isOptionDisabled = useCallback(
    (option: SelectOption) => {
      return disabledOptions.includes(option.id)
    },
    [disabledOptions]
  )

  const handleToggle = useCallback(
    (option: SelectOption, event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      if (isOptionDisabled(option)) {
        return
      }

      if (multiple) {
        const currentSelected = Array.isArray(value) ? value : []

        if (isSelected(option)) {
          const newSelected = currentSelected.filter(
            item => item.id !== option.id
          )
          onValueChange(newSelected.length > 0 ? newSelected : null)
        } else {
          if (canSelectMore()) {
            const newSelected = [...currentSelected, option]
            onValueChange(newSelected)
          }
        }
      } else {
        onValueChange(option)
        setOpen(false)
        setSearchTerm('')
      }
    },
    [
      multiple,
      value,
      onValueChange,
      isSelected,
      canSelectMore,
      isOptionDisabled,
    ]
  )

  const removeItem = useCallback(
    (optionToRemove: SelectOption, event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (isOptionDisabled(optionToRemove)) {
        return
      }

      if (multiple && Array.isArray(value)) {
        const newSelected = value.filter(item => item.id !== optionToRemove.id)
        onValueChange(newSelected.length > 0 ? newSelected : null)
      }
    },
    [multiple, value, onValueChange, isOptionDisabled]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onValueChange(null)
    },
    [onValueChange]
  )

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
      if (newOpen && !hasInitiallyLoaded) {
        refetch()
      }
    },
    [hasInitiallyLoaded, refetch]
  )

  const defaultRenderOption = useCallback(
    (option: SelectOption) => {
      const isDisabled = isOptionDisabled(option)

      return (
        <div
          className={cn(
            'flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-colors w-full',
            !multiple && 'px-3 py-2.5',
            isDisabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50 cursor-pointer',
            !canSelectMore() &&
              !isSelected(option) &&
              multiple &&
              'opacity-50 cursor-not-allowed'
          )}
        >
          {multiple && (
            <Checkbox
              checked={isSelected(option)}
              disabled={(!canSelectMore() && !isSelected(option)) || isDisabled}
            />
          )}

          {showAvatar && (
            <Avatar className="h-8 w-8">
              <AvatarImage src={option.avatar} />
              <AvatarFallback className="text-xs">
                {option.label
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}

          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900 truncate">
              {option.label}
            </div>
            {option.subtitle && (
              <div className="text-xs text-gray-500 truncate">
                {option.subtitle}
              </div>
            )}
          </div>
        </div>
      )
    },
    [showAvatar, multiple, isSelected, canSelectMore, isOptionDisabled]
  )

  const defaultRenderValue = useCallback(
    (valueToRender: SelectOption | SelectOption[]) => {
      if (multiple && Array.isArray(valueToRender)) {
        if (valueToRender.length === 0) return null

        return <span className="truncate">{valueToRender.length} selected</span>
      }

      // Single select rendering
      const singleValue = Array.isArray(valueToRender)
        ? valueToRender[0]
        : valueToRender
      if (!singleValue) return null

      return (
        <div className="flex items-center gap-2 flex-1">
          {showAvatar && (
            <Avatar className="h-6 w-6">
              <AvatarImage src={singleValue.avatar} />
              <AvatarFallback className="text-xs">
                {singleValue.label
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}
          <span className="truncate">{singleValue.label}</span>
        </div>
      )
    },
    [multiple, showAvatar]
  )

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined

  if (multiple) {
    return (
      <div className="space-y-2">
        {label && (
          <Label
            className="text-sm font-medium text-ss-neutral-950"
            variant={required ? 'required' : 'default'}
          >
            {label}
          </Label>
        )}

        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={cn(
                'w-full justify-between h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500',
                !hasValue && 'text-muted-foreground',
                error && 'border-ss-critical-200',
                disabled && 'opacity-50 cursor-not-allowed',
                className
              )}
            >
              {(() => {
                if (hasValue) {
                  return renderValue
                    ? renderValue(value!)
                    : defaultRenderValue(value!)
                }
                return <span className="truncate">{placeholder}</span>
              })()}
              <ChevronDown className="h-4 w-4 text-ss-neutral-600" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="max-h-80 overflow-hidden p-0 w-[370px]"
            align="start"
            onCloseAutoFocus={e => e.preventDefault()}
          >
            {/* Search Input */}
            {enableSearch && (
              <div className="relative p-3 pb-1">
                <Search className="absolute left-5 top-7 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-7 pr-3 w-full h-8"
                  onClick={e => e.stopPropagation()}
                  onKeyDownCapture={e => e.stopPropagation()}
                />
              </div>
            )}

            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {isLoading && options.length === 0 && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-gray-500">Loading...</span>
                </div>
              )}

              {!isLoading && queryError && (
                <div className="py-4 text-center">
                  <p className="text-sm text-ss-critical-200 mb-2">
                    Error loading options
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="h-8"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {!isLoading && !queryError && options.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  {debouncedSearchTerm ? noResultsMessage : emptyMessage}
                </div>
              )}

              {!isLoading && !queryError && options.length > 0 && (
                <>
                  {options.map(option => (
                    <DropdownMenuItem
                      key={option.id}
                      onClick={e => handleToggle(option, e)}
                      className="cursor-pointer"
                      onSelect={e => e.preventDefault()}
                    >
                      {renderOption
                        ? renderOption(option)
                        : defaultRenderOption(option)}
                    </DropdownMenuItem>
                  ))}

                  {hasNextPage && (
                    <div className="border-t">
                      <div ref={observerRef} className="h-1" />

                      <div className="py-2 text-center">
                        {isFetchingNextPage ? (
                          <div className="flex items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="ml-2 text-sm text-gray-500">
                              Loading more...
                            </span>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-xs text-gray-400">
                              Scroll for more
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchNextPage()}
                              className="h-8 text-xs text-blue-600 hover:text-blue-800"
                            >
                              Load More
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Selected chips - matching MultiSelect design */}
        {selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedOptions.map(option => {
              const isDisabled = isOptionDisabled(option)

              return (
                <div
                  key={option.id}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 border text-xs font-medium rounded-full',
                    isDisabled
                      ? 'border-ss-neutral-300 text-ss-neutral-600 bg-ss-neutral-50'
                      : 'border-ss-neutral-950 text-ss-neutral-950'
                  )}
                >
                  <span className="truncate max-w-[120px]">{option.label}</span>
                  {!disabled && !isDisabled && (
                    <button
                      onClick={e => removeItem(option, e)}
                      className="hover:bg-blue-100 rounded-full p-0.5"
                      type="button"
                      aria-label={`Remove ${option.label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {error && <p className="text-sm text-ss-critical-200 mt-1">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label
          className="text-sm font-medium text-ss-neutral-950"
          variant={required ? 'required' : 'default'}
        >
          {label}
        </Label>
      )}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500',
              !hasValue && 'text-muted-foreground',
              error && 'border-ss-critical-200',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
          >
            <div className="flex items-center flex-1 min-w-0 overflow-hidden">
              {(() => {
                if (hasValue) {
                  const displayValue = renderValue
                    ? renderValue(value!)
                    : defaultRenderValue(value!)
                  return (
                    <>
                      {showTooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {displayValue}
                          </TooltipTrigger>
                          <TooltipContent side="bottom" align="center">
                            {displayValue}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        displayValue
                      )}
                    </>
                  )
                }
                return <span className="truncate">{placeholder}</span>
              })()}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {hasValue && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="hover:bg-ss-neutral-100 rounded-full p-1 transition-colors"
                  tabIndex={-1}
                  aria-label="Clear selection"
                >
                  <X className="h-3 w-3 opacity-50 hover:opacity-100" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          {enableSearch && (
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 border-ss-neutral-200 focus:border-ss-blue-500"
                />
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {isLoading && options.length === 0 && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Loading...</span>
              </div>
            )}

            {!isLoading && queryError && (
              <div className="py-4 text-center">
                <p className="text-sm text-ss-critical-200 mb-2">
                  Error loading options
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="h-8"
                >
                  Retry
                </Button>
              </div>
            )}

            {!isLoading && !queryError && options.length === 0 && (
              <div className="py-4 text-center text-sm text-gray-500">
                {debouncedSearchTerm ? noResultsMessage : emptyMessage}
              </div>
            )}

            {!isLoading && !queryError && options.length > 0 && (
              <>
                {options.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleToggle(option)}
                    className="cursor-pointer w-full text-left"
                    aria-label={`Select ${option.label}`}
                  >
                    {renderOption
                      ? renderOption(option)
                      : defaultRenderOption(option)}
                  </button>
                ))}

                {hasNextPage && (
                  <div className="border-t">
                    <div ref={observerRef} className="h-1" />
                    <div className="py-2 text-center">
                      {isFetchingNextPage ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="ml-2 text-sm text-gray-500">
                            Loading more...
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="text-xs text-gray-400">
                            Scroll for more
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchNextPage()}
                            className="h-8 text-xs text-blue-600 hover:text-blue-800"
                          >
                            Load More
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {error && <p className="text-sm text-ss-critical-200 mt-1">{error}</p>}
    </div>
  )
}
